import {ExplainerTitleVariant} from "pages/Front/Booking/Credits/CreditCalculator/types";
import {useCreditCalculator} from "pages/Front/Booking/Credits/CreditCalculator/useCreditCalculator";
import {CloseButton} from "pages/Front/Booking/Credits/CreditCalculator/components/CloseButton";
import {CreditsBadge} from "pages/Front/Booking/Credits/CreditCalculator/components/CreditsBadge";
import {CREDITS_CLASSES, EXPLAINER_TITLES} from "pages/Front/Booking/Credits/CreditCalculator/constants";
import {RideCards} from "pages/Front/Booking/Credits/CreditCalculator/components/RideCards";
import {CreditCalculator} from "pages/Front/Booking/Credits/CreditCalculator/components/CreditCalculator";
import {PackLadder} from "pages/Front/Booking/Credits/CreditCalculator/components/PackLadder";
import {CreditRuleCards} from "pages/Front/Booking/Credits/CreditCalculator/components/CreditRuleCards";
import React from "react";

interface CreditsExplainerPageProps {
    /**
     * "single" → "Wie viele Credits brauche ich?" (standard flow)
     * "group"  → "Wie viele Credits brauchen wir?" (family / group flows)
     */
    titleVariant?: ExplainerTitleVariant;
    /**
     * When provided, the page renders in popup mode: the brand close button
     * appears top-right (40px on mobile, 50px from sm upward) and invokes this
     * callback. Omit for inline/page usage.
     */
    onClose?: () => void;
}

export function CreditsExplainerPage({titleVariant = "single", onClose}: CreditsExplainerPageProps) {
    const calculator = useCreditCalculator();

    return (
        <div className="flex flex-col h-[85vh] relative mx-auto max-w-3xl rounded-xl border border-line bg-white overflow-auto">
            {onClose && (
                <>
                    <CloseButton onClose={onClose} size={40} className="absolute right-3 top-3 sm:hidden" />
                    <CloseButton onClose={onClose} size={50} className="absolute right-5 top-5 hidden sm:inline-flex" />
                </>
            )}
            <header className="px-4 pt-4 sm:px-5 sm:pt-5">
                <div
                    className={`flex items-start gap-3.5 rounded-xl bg-gradient-to-r from-brand-gold to-brand-orange px-4 py-4 sm:px-5 sm:py-5 ${
                        onClose ? "pr-12 sm:pr-16" : ""
                    }`}
                >
                    <CreditsBadge size="lg" />
                    <div>
                        <h2 className="text-xl font-bold text-ink">{EXPLAINER_TITLES[titleVariant]}</h2>
                        <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-ink-warm">
                            Credits sind die Währung der <span className="font-medium text-ink">Speed &amp; Drift Zone</span>. Dein Eintrittsticket deckt über
                            20 Attraktionen ab — Go-Karting und Drift bezahlst du mit Credits. <span className="text-ink">1 Credit = CHF 1.00</span>, grössere
                            Pakete sind günstiger.
                        </p>
                    </div>
                </div>
            </header>

            <div className="space-y-4 px-4 pb-4 pt-4 sm:px-5 sm:pb-5">
                <RideCards />
                <CreditCalculator calculator={calculator} />
                <PackLadder highlightedPackCredits={calculator.recommendation?.pack.credits ?? null} />
                <CreditRuleCards />
            </div>

            <p className="sr-only" aria-live="polite">
                {calculator.creditsNeeded > 0 && calculator.recommendation ? `Empfehlung: ${calculator.recommendation.pack.credits} Credits` : undefined}
            </p>
            <span className={`hidden ${CREDITS_CLASSES.ring}`} aria-hidden="true" />
        </div>
    );
}
